<template>
    <div class="article-option-group">
        <div class="name">{{ getNameInLocationLanguage(optionGroup, props.showInternalNames) }}</div>
        <div class="instruction">{{ instructionText }}</div>
        <div class="options">
            <template v-for="option in optionGroup.options" :key="option.articleId">
                <ArticleOption
                    ref="articleOptionComponents"
                    :option="option"
                    :mainArticleId="props.mainArticleId"
                    :isRadio="isRadio"
                    :showPlusOnPrices="showPlusOnPrices"
                    :maxPerOption="optionGroup.maxPerOption"
                    :isNewOptionAllowed="isNewOptionAllowed"
                    :isMaxSumReached="isMaxSumReached"
                    :optionGroupId="optionGroup.optionGroupId"
                    :showInternalNames="props.showInternalNames"
                    :showOohPrices="props.showOohPrices"
                    :showItemImages="props.showItemImages"
                    @selected="onOptionSelected"
                    @changed="onOptionChanged"
                />
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { IArticleWithOptions } from "@/data/commands/customer/AddArticleToCartCmd";
    import type { ICustomerOptionGroup, ICustomerOptionGroupOption } from "@/data/model/Customer";
    import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
    import { translations } from "@/shared/localization/translations";
    import { computed, onMounted, ref } from "vue";

    import ArticleOption from "./ArticleOption.vue";

    // define properties
    const props = defineProps<{
        optionGroup: ICustomerOptionGroup;
        // the top-level main item the options are offered in, also for the groups of an option
        mainArticleId: string;
        showPlusOnPrices: boolean;
        showInternalNames: boolean;
        showOohPrices: boolean;
        showItemImages: boolean;
    }>();

    // define emits
    const emit = defineEmits<{
        changed: [option: ICustomerOptionGroupOption];
    }>();

    // define template refs
    const articleOptionComponents = ref<InstanceType<typeof ArticleOption>[]>([]);

    // define computed
    const isRadio = computed(() => props.optionGroup.minOptions == 1 && props.optionGroup.maxOptions == 1);

    // todo: this is a mess....
    const instructionText = computed(() => {
        const optionGroup = props.optionGroup;
        const q1 =
            optionGroup.minOptions === undefined || optionGroup.minOptions === 0 ? undefined : optionGroup.minOptions;

        let instructionText = "";
        if (
            q1 === undefined &&
            (optionGroup.maxOptions === undefined ||
                optionGroup.maxOptions === 1 ||
                optionGroup.maxOptions === optionGroup.options.length)
        ) {
            instructionText += translations.addToCartPopover.optional + ".";
        } else if (q1 !== undefined && optionGroup.minOptions === optionGroup.maxOptions) {
            instructionText +=
                translations.addToCartPopover.chooseExact
                    .replace("{0}", q1.toString())
                    .replace(
                        "{1}",
                        q1 > 1 ? translations.addToCartPopover.optionPlur : translations.addToCartPopover.optionSing
                    ) + ".";
        } else if (optionGroup.maxOptions === undefined && q1 !== undefined) {
            instructionText +=
                translations.addToCartPopover.chooseMin
                    .replace("{0}", q1.toString())
                    .replace(
                        "{1}",
                        q1 > 1 ? translations.addToCartPopover.optionPlur : translations.addToCartPopover.optionSing
                    ) + ".";
        } else if (optionGroup.maxOptions !== undefined && q1 === undefined)
            instructionText +=
                translations.addToCartPopover.chooseUpTo
                    .replace("{0}", optionGroup.maxOptions + "")
                    .replace(
                        "{1}",
                        optionGroup.maxOptions > 1
                            ? translations.addToCartPopover.optionPlur
                            : translations.addToCartPopover.optionSing
                    ) + ".";
        else if (optionGroup.maxOptions !== undefined && q1 !== undefined)
            instructionText +=
                translations.addToCartPopover.chooseBetween
                    .replace("{0}", q1.toString())
                    .replace("{1}", optionGroup.maxOptions + "") + ".";

        if (
            optionGroup.maxSum !== undefined &&
            optionGroup.maxSum > 1 &&
            optionGroup.options.length > 1 &&
            (optionGroup.maxOptions === undefined || optionGroup.maxOptions > 1) &&
            (optionGroup.maxPerOption === undefined || optionGroup.maxPerOption > 1)
        ) {
            instructionText +=
                " " + translations.addToCartPopover.upToTotalItems.replace("{0}", optionGroup.maxSum + "") + ".";
        }

        if (instructionText.length !== 0) instructionText = instructionText.substring(0, instructionText.length - 1);

        return instructionText;
    });

    const selectedOptions = computed(() => {
        const result = [] as InstanceType<typeof ArticleOption>[];
        for (const articleOptionComponent of articleOptionComponents.value) {
            if (articleOptionComponent.isSelected) {
                result.push(articleOptionComponent);
            }
        }
        return result;
    });

    const totalSelected = computed(() => selectedOptions.value.length);
    const totalQuantity = computed(() => {
        let quantitySum = 0;
        for (const articleOptionComponent of selectedOptions.value) {
            quantitySum += articleOptionComponent.quantity;
        }
        return quantitySum;
    });
    const totalPrice = computed(() => {
        let sum = 0;
        for (const articleOptionComponent of selectedOptions.value) {
            sum += articleOptionComponent.totalAmount;
        }
        return sum;
    });
    const isValid = computed(() => {
        if (props.optionGroup.minOptions !== undefined && props.optionGroup.minOptions > totalSelected.value)
            return false;

        if (props.optionGroup.maxOptions !== undefined && props.optionGroup.maxOptions < totalSelected.value)
            return false;

        for (const articleOptionComponent of articleOptionComponents.value) {
            if (!articleOptionComponent.isValid) return false;
        }

        return true;
    });
    const isMaxSumReached = computed(() => {
        return props.optionGroup.maxSum !== undefined && totalQuantity.value >= props.optionGroup.maxSum;
    });
    const isMaxOptionsReached = computed(() => {
        return props.optionGroup.maxOptions !== undefined && totalSelected.value >= props.optionGroup.maxOptions;
    });
    const isNewOptionAllowed = computed(() => {
        if (isRadio.value) {
            return true;
        }

        if (isMaxSumReached.value) {
            return false;
        }

        if (isMaxOptionsReached.value) {
            return false;
        }

        return true;
    });

    // define methods
    function onOptionSelected(option: ICustomerOptionGroupOption) {
        if (!isRadio.value) return;

        // deselect all other radio buttons
        for (const articleOptionComponent of articleOptionComponents.value) {
            if (articleOptionComponent.option.articleId != option.articleId) {
                articleOptionComponent.deselect();
            }
        }
    }

    function getSelectedArticlesWithOptions(): IArticleWithOptions[] {
        const result = [] as IArticleWithOptions[];

        for (const selectedOption of selectedOptions.value) {
            result.push(selectedOption.getArticleWithOptions());
        }

        return result;
    }

    function onOptionChanged(option: ICustomerOptionGroupOption) {
        emit("changed", option);
    }

    // define callbacks
    onMounted(() => {
        if (
            props.optionGroup.maxOptions !== undefined &&
            props.optionGroup.maxOptions > props.optionGroup.options.length
        ) {
            // eslint-disable-next-line vue/no-mutating-props
            props.optionGroup.maxOptions = props.optionGroup.options.length;
        }
    });

    // define exposed
    defineExpose({
        totalPrice,
        isValid,
        getSelectedArticlesWithOptions,
    });
</script>

<style scoped lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";

    .article-option-group {
        color: #484848;
        padding: 8px 0;

        > .name {
            font-size: 12px;
            font-weight: 600;
            color: colors.$fontColor;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            padding-bottom: 3px;
        }

        > .instruction {
            font-size: 12px;
            font-weight: 300;
            color: #aaaaaa;
            line-height: 16px;
        }

        > .options {
            width: 100%;
            padding: 4px 0 0 0;
        }
    }
</style>
